import axiosInstance from "./axiosConfig";

// One Server-Sent Events stream per browser tab, shared by everything that
// wants live updates (the notification bell, the messages badge and page).
//
// - Uses fetch with the normal Authorization header (EventSource cannot send
//   headers), so no token ever appears in a URL.
// - The stream is only open while someone is subscribed AND the tab is
//   visible. Hidden tabs hold no connection; when the tab becomes visible
//   again it reconnects and emits "resume" so subscribers re-read their data.
// - Events are hints ("messages", "notifications"): subscribers re-read the
//   cheap count endpoints, so a missed or duplicate event is harmless.
// - Reconnects with exponential backoff (1s up to 30s). The server also ends
//   a stream every ~15 minutes to force re-authentication.

const MAX_BACKOFF_MS = 30000;

const listeners = new Map(); // event name -> Set<fn>
let controller = null;
let timer = null;
let backoff = 1000;
let listening = false; // visibility/online handlers installed

const hasSubscribers = () => [...listeners.values()].some((s) => s.size > 0);
const visible = () => typeof document === "undefined" || !document.hidden;

function emit(event) {
  const set = listeners.get(event);
  if (set) set.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error("realtime listener failed:", err);
    }
  });
}

function disconnect() {
  if (timer) clearTimeout(timer);
  timer = null;
  if (controller) controller.abort();
  controller = null;
}

function scheduleReconnect(delay) {
  if (timer || !hasSubscribers() || !visible()) return;
  timer = setTimeout(() => {
    timer = null;
    connect();
  }, delay);
}

async function connect() {
  if (controller || !hasSubscribers() || !visible()) return;
  const token = localStorage.getItem("token");
  if (!token) {
    scheduleReconnect(MAX_BACKOFF_MS); // not signed in yet; look again later
    return;
  }
  const ctl = new AbortController();
  controller = ctl;
  try {
    const res = await fetch(`${axiosInstance.defaults.baseURL}/events`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
      signal: ctl.signal,
    });
    if (res.status !== 200 || !res.body) {
      controller = null;
      scheduleReconnect(res.status === 401 ? MAX_BACKOFF_MS : backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let i;
      while ((i = buf.indexOf("\n\n")) >= 0) {
        const block = buf.slice(0, i);
        buf = buf.slice(i + 2);
        const m = /^event: (.+)$/m.exec(block);
        if (m) {
          if (m[1] === "ready") backoff = 1000; // healthy connection
          emit(m[1]);
        }
      }
    }
  } catch (err) {
    if (ctl.signal.aborted) return; // closed on purpose
  }
  if (controller === ctl) controller = null;
  scheduleReconnect(backoff);
  backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
}

function onVisibility() {
  if (!hasSubscribers()) return;
  if (visible()) {
    backoff = 1000;
    connect();
    emit("resume"); // catch up on anything missed while hidden
  } else {
    disconnect(); // a hidden tab holds no connection
  }
}

const onOnline = () => {
  if (hasSubscribers() && visible() && !controller) {
    backoff = 1000;
    connect();
  }
};

function install() {
  if (listening) return;
  listening = true;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
}

function uninstall() {
  if (!listening) return;
  listening = false;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("online", onOnline);
}

// Subscribe to an event ("messages", "notifications", "ready", "resume").
// Returns the unsubscribe function.
export function subscribe(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  install();
  connect();
  return () => {
    const set = listeners.get(event);
    if (set) set.delete(fn);
    if (!hasSubscribers()) {
      disconnect();
      uninstall();
    }
  };
}
